import { ShieldCheck } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const LAST_UPDATED = "28 August 2026";

const PrivacyPolicy = () => {
  const { locale } = useI18n();
  const isIt = locale === "it";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="page-container mx-auto py-12 space-y-8 max-w-4xl">
        <div className="glass-card p-8 space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold">{isIt ? "Informativa sulla Privacy" : "Privacy Policy"}</h1>
          <p className="text-sm text-muted-foreground">
            {isIt ? "Ultimo aggiornamento" : "Last updated"}: {LAST_UPDATED}
          </p>
        </div>

        <div className="glass-card p-8 space-y-6 text-sm leading-relaxed text-muted-foreground">
          {isIt ? (
            <>
              <Section title="1. Introduzione">
                NATIBOT ("noi", "nostro") rispetta la tua privacy e si impegna a proteggere i dati personali che
                raccogliamo quando utilizzi il nostro bot Discord, il sito web e la dashboard associata
                (collettivamente, il "Servizio").
              </Section>
              <Section title="2. Dati raccolti">
                Raccogliamo: identificativi Discord (user ID, username, avatar), ID e nomi dei server in cui il bot
                è installato, contenuti dei messaggi strettamente necessari al funzionamento dei plugin attivati
                (es. moderazione, leveling, ticket), impostazioni di configurazione scelte dagli amministratori del
                server e dati tecnici (indirizzo IP, log di accesso) per finalità di sicurezza.
              </Section>
              <Section title="3. Finalità del trattamento">
                Utilizziamo i dati per: fornire ed erogare le funzionalità del bot, mantenere la sicurezza e
                prevenire abusi, migliorare il Servizio, fornire assistenza tecnica e rispettare obblighi di legge.
              </Section>
              <Section title="4. Condivisione dei dati">
                Non vendiamo i tuoi dati personali. I dati possono essere condivisi con fornitori di infrastruttura
                (hosting, database) esclusivamente per l'erogazione del Servizio e nel rispetto di accordi di
                riservatezza.
              </Section>
              <Section title="5. Conservazione dei dati">
                Conserviamo i dati per il tempo necessario a fornire il Servizio o secondo quanto richiesto dalla
                legge applicabile. Gli amministratori di server possono richiedere la cancellazione dei dati relativi
                al proprio server rimuovendo il bot o contattandoci.
              </Section>
              <Section title="6. I tuoi diritti">
                In base al GDPR e alle normative applicabili, hai diritto di accesso, rettifica, cancellazione,
                limitazione e portabilità dei tuoi dati personali. Per esercitare questi diritti contattaci tramite
                la pagina{" "}
                <a href="/support/contact" className="text-primary hover:underline">
                  Contattaci
                </a>
                .
              </Section>
              <Section title="7. Sicurezza">
                Adottiamo misure tecniche e organizzative adeguate per proteggere i dati da accessi non autorizzati,
                perdita o divulgazione.
              </Section>
              <Section title="8. Modifiche a questa informativa">
                Potremmo aggiornare periodicamente questa informativa. Le modifiche sostanziali saranno comunicate
                tramite il nostro server Discord o il sito web.
              </Section>
              <Section title="9. Contatti">
                Per domande relative a questa informativa, contattaci tramite la pagina{" "}
                <a href="/support/contact" className="text-primary hover:underline">
                  Contattaci
                </a>
                .
              </Section>
            </>
          ) : (
            <>
              <Section title="1. Introduction">
                NATIBOT ("we", "our") respects your privacy and is committed to protecting the personal data we
                collect when you use our Discord bot, website, and associated dashboard (collectively, the
                "Service").
              </Section>
              <Section title="2. Data We Collect">
                We collect: Discord identifiers (user ID, username, avatar), IDs and names of servers where the bot
                is installed, message content strictly necessary for the operation of enabled plugins (e.g.
                moderation, leveling, tickets), configuration settings chosen by server administrators, and technical
                data (IP address, access logs) for security purposes.
              </Section>
              <Section title="3. Purpose of Processing">
                We use data to: provide and deliver the bot's features, maintain security and prevent abuse, improve
                the Service, provide technical support, and comply with legal obligations.
              </Section>
              <Section title="4. Data Sharing">
                We do not sell your personal data. Data may be shared with infrastructure providers (hosting,
                databases) solely to deliver the Service and under confidentiality agreements.
              </Section>
              <Section title="5. Data Retention">
                We retain data for as long as necessary to provide the Service or as required by applicable law.
                Server administrators may request deletion of data related to their server by removing the bot or
                contacting us.
              </Section>
              <Section title="6. Your Rights">
                Under GDPR and applicable regulations, you have the right to access, rectify, erase, restrict, and
                port your personal data. To exercise these rights, contact us via the{" "}
                <a href="/support/contact" className="text-primary hover:underline">
                  Contact Us
                </a>{" "}
                page.
              </Section>
              <Section title="7. Security">
                We implement appropriate technical and organizational measures to protect data from unauthorized
                access, loss, or disclosure.
              </Section>
              <Section title="8. Changes to This Policy">
                We may update this policy periodically. Material changes will be communicated via our Discord
                server or website.
              </Section>
              <Section title="9. Contact">
                For questions about this policy, contact us via the{" "}
                <a href="/support/contact" className="text-primary hover:underline">
                  Contact Us
                </a>{" "}
                page.
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-2">
    <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    <p>{children}</p>
  </div>
);

export default PrivacyPolicy;
